import React, { useEffect } from "react";

/**
 * v1.31.66 — Screen-wide fireworks burst (no card, no dismiss button).
 * Several shells launch at staggered times and explode into radial particles
 * that arc and fall with gravity, then the overlay unmounts itself (~2.8s).
 * Used for the "slip approved/verified" moment.
 */
export default function FireworksBurst({ open, onClose, bursts = 8 }) {
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(onClose, 2800);
        return () => clearTimeout(t);
    }, [open, onClose]);

    if (!open) return null;

    const colors = ["#fbbf24", "#34d399", "#f472b6", "#38bdf8", "#e879f9", "#818cf8", "#f87171"];
    const PARTICLES = 24;

    return (
        <div className="pointer-events-none fixed inset-0 z-[110] overflow-hidden" data-testid="fireworks-burst">
            {Array.from({ length: bursts }).map((_, b) => {
                const left = 10 + (b * 73) % 80;
                const top = 16 + (b * 37) % 46;
                const delay = (b * 340) % 2100;
                const color = colors[b % colors.length];
                const color2 = colors[(b + 3) % colors.length];
                const spread = 95 + (b * 19) % 70;
                return (
                    <div key={b} className="absolute" style={{ left: `${left}%`, top: `${top}%` }}>
                        {Array.from({ length: PARTICLES }).map((_, i) => {
                            const ang = (i / PARTICLES) * Math.PI * 2;
                            const tx = Math.cos(ang) * spread;
                            const ty = Math.sin(ang) * spread;
                            const c = i % 2 ? color : color2;
                            return (
                                <span
                                    key={i}
                                    style={{
                                        position: "absolute", left: 0, top: 0,
                                        width: 6, height: 6, borderRadius: "9999px",
                                        background: c, boxShadow: `0 0 8px 2px ${c}`,
                                        "--tx": `${tx}px`, "--ty": `${ty}px`,
                                        animation: `fw-explode 1500ms ease-out ${delay}ms forwards`,
                                    }}
                                />
                            );
                        })}
                        <span
                            style={{
                                position: "absolute", left: -4, top: -4, width: 10, height: 10, borderRadius: "9999px",
                                background: color, boxShadow: `0 0 20px 7px ${color}`,
                                animation: `fw-flash 520ms ease-out ${delay}ms forwards`,
                            }}
                        />
                    </div>
                );
            })}
            <style>{`
                @keyframes fw-explode {
                    0%   { transform: translate(0,0) scale(1); opacity: 0; }
                    8%   { opacity: 1; }
                    70%  { opacity: 1; }
                    100% { transform: translate(var(--tx), calc(var(--ty) + 40px)) scale(0.3); opacity: 0; }
                }
                @keyframes fw-flash {
                    0%   { transform: scale(0.2); opacity: 0.95; }
                    100% { transform: scale(2.6); opacity: 0; }
                }
            `}</style>
        </div>
    );
}
